import React, { useRef, useState, useCallback, useEffect } from 'react';
import jsQR from 'jsqr';
import styles from './OperatorScanModal.module.scss';

/**
 * Модальный сканер для операторов баз.
 *
 * Шаги:
 *  idle         — плашки (QR + транспорт), кнопка заблокирована
 *  scanning_qr  — живая камера (задняя), авто-сканирование QR
 *  loading      — запрос к бэку
 *  data         — данные водителя + плашка фото транспорта
 *  done         — успех
 *  error        — ошибка + кнопка «Назад»
 *  warning      — предупреждение + кнопка «Назад»
 */
const OperatorScanModal = ({ onClose, onQRScanned, onConfirm, ensureGeo }) => {
  const [step, setStep] = useState('idle');
  const [driverData, setDriverData] = useState(null);
  const [lastLoadA, setLastLoadA] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [warningMsg, setWarningMsg] = useState('');
  const [transportImageSrc, setTransportImageSrc] = useState(null);
  const [transportBlob, setTransportBlob] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [cameraError, setCameraError] = useState('');

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanningRef = useRef(false);
  const rafRef = useRef(null);
  const photoInputRef = useRef(null);
  // Храним обработчик QR в ref, чтобы не было circular-зависимостей в useCallback
  const onQrDetectedRef = useRef(null);

  // ── Остановка камеры ─────────────────────────────────────────────────────
  const stopCamera = useCallback(() => {
    scanningRef.current = false;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.srcObject = null; }
  }, []);

  // ── Запуск задней камеры ─────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setCameraError('');
    let stream = null;
    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        });
      } catch (_) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } } });
        } catch (_2) {
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
        }
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', true);
        await videoRef.current.play();
      }

      // Петля сканирования
      scanningRef.current = true;
      const loop = () => {
        if (!scanningRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || video.readyState < video.HAVE_ENOUGH_DATA) {
          rafRef.current = requestAnimationFrame(loop);
          return;
        }
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'attemptBoth' });
        if (code?.data) {
          // QR найден — останавливаем камеру и обрабатываем
          onQrDetectedRef.current?.(code.data.trim());
          return;
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    } catch (err) {
      const msg = err.name === 'NotAllowedError'
        ? 'Доступ к камере запрещён. Разрешите в настройках браузера.'
        : 'Не удалось открыть камеру. Попробуйте снова.';
      setCameraError(msg);
      setStep('error');
    }
  }, []);

  // ── Обработчик найденного QR ─────────────────────────────────────────────
  const handleQrDetected = useCallback(async (uid) => {
    stopCamera();
    setStep('loading');
    try {
      const result = await onQRScanned(uid);
      if (result.error) { setErrorMsg(result.error); setStep('error'); }
      else if (result.warning) { setWarningMsg(result.warning); setStep('warning'); }
      else { setDriverData(result.driverData); setLastLoadA(result.lastLoadA); setStep('data'); }
    } catch {
      setErrorMsg('Ошибка загрузки данных водителя. Попробуйте снова.');
      setStep('error');
    }
  }, [stopCamera, onQRScanned]);

  // Обновляем ref при изменении колбэка (избегаем stale closure в петле)
  onQrDetectedRef.current = handleQrDetected;

  // Запускаем/останавливаем камеру вместе с шагом scanning_qr
  useEffect(() => {
    if (step === 'scanning_qr') startCamera();
    else stopCamera();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Останавливаем при unmount
  useEffect(() => () => stopCamera(), [stopCamera]);

  // ── Фото транспорта ───────────────────────────────────────────────────────
  const handlePhotoFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTransportImageSrc(URL.createObjectURL(file));
    setTransportBlob(file);
    e.target.value = '';
  }, []);

  // ── Отправка ─────────────────────────────────────────────────────────────
  const handleConfirm = useCallback(async () => {
    if (!transportBlob || !lastLoadA?.id) return;
    setSending(true);
    setSendError('');
    try {
      let geo = '';
      try { geo = await ensureGeo(); } catch (_) {}
      await onConfirm({ loadId: lastLoadA.id, driverData, photoBlob: transportBlob, geo });
      setStep('done');
    } catch (err) {
      setSendError('Ошибка отправки: ' + (err?.message || 'Неизвестная ошибка'));
    } finally {
      setSending(false);
    }
  }, [transportBlob, lastLoadA, driverData, ensureGeo, onConfirm]);

  // ── Сброс ─────────────────────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    stopCamera();
    setStep('idle');
    setDriverData(null); setLastLoadA(null);
    setErrorMsg(''); setWarningMsg('');
    setTransportImageSrc(null); setTransportBlob(null);
    setSendError(''); setCameraError('');
  }, [stopCamera]);

  const handleClose = useCallback(() => { handleReset(); onClose(); }, [handleReset, onClose]);

  const isStep1Done = ['data', 'sending', 'done'].includes(step);
  const isStep2Done = ['sending', 'done'].includes(step);
  const isStep3Done = step === 'done';

  // ── Рендер ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} ${step === 'scanning_qr' ? styles.modalFullscreen : ''}`}>

        {/* Шапка */}
        <div className={styles.header}>
          <span className={styles.title}>Отметка прибытия</span>
          <button className={styles.closeBtn} onClick={handleClose}>✕</button>
        </div>

        {/* Шаги (скрываем во время сканирования) */}
        {step !== 'scanning_qr' && (
          <>
            <div className={styles.steps}>
              <StepDot num={1} active={step === 'idle' || step === 'loading'} done={isStep1Done} />
              <div className={styles.stepLine} />
              <StepDot num={2} active={step === 'data'} done={isStep2Done} />
              <div className={styles.stepLine} />
              <StepDot num={3} active={step === 'sending'} done={isStep3Done} />
            </div>
            <div className={styles.stepLabels}>
              <span>Сканировать QR</span>
              <span>Фото транспорта</span>
              <span>Подтвердить</span>
            </div>
          </>
        )}

        {/* ── Живая камера QR ─────────────────────────────────────────────── */}
        {step === 'scanning_qr' && (
          <div className={styles.scannerWrap}>
            <video ref={videoRef} className={styles.scannerVideo} playsInline muted autoPlay />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div className={styles.scannerOverlay}>
              <div className={styles.scannerFrame}>
                <span className={styles.corner} />
                <span className={styles.corner} />
                <span className={styles.corner} />
                <span className={styles.corner} />
              </div>
              <p className={styles.scanHint}>Наведите на QR-код — распознавание автоматическое</p>
            </div>
            <button className={styles.scanBackBtn} onClick={() => { stopCamera(); setStep('idle'); }}>← Назад</button>
          </div>
        )}

        {/* ── Основное тело ───────────────────────────────────────────────── */}
        {step !== 'scanning_qr' && (
          <div className={styles.body}>

            {/* Idle: плашки */}
            {step === 'idle' && (
              <div className={styles.cards}>
                <div className={`${styles.card} ${styles.cardActive}`} onClick={() => setStep('scanning_qr')}>
                  <span className={styles.cardIcon}>🔲</span>
                  <span className={styles.cardLabel}>Нажмите для сканирования QR</span>
                  <span className={styles.cardSub}>Откроется камера</span>
                </div>
                <div className={`${styles.card} ${styles.cardDisabled}`}>
                  <span className={styles.cardIcon}>🚛</span>
                  <span className={styles.cardLabel}>Фото транспорта</span>
                  <span className={styles.cardSub}>Сначала сканируйте QR</span>
                </div>
                <button className={styles.confirmBtn} disabled>Подтвердить прибытие</button>
              </div>
            )}

            {/* Loading */}
            {step === 'loading' && (
              <div className={styles.infoBlock}>
                <div className={styles.spinner} />
                <p className={styles.infoText}>Загрузка данных водителя...</p>
              </div>
            )}

            {/* Error */}
            {step === 'error' && (
              <div className={styles.infoBlock}>
                <span className={styles.infoIcon}>❌</span>
                <p className={styles.infoText}>{errorMsg || cameraError}</p>
                <div className={styles.btnRow}>
                  <button className={styles.backBtn} onClick={handleReset}>← Назад</button>
                </div>
              </div>
            )}

            {/* Warning */}
            {step === 'warning' && (
              <div className={styles.infoBlock}>
                <span className={styles.infoIcon}>⚠️</span>
                <p className={styles.infoText}>{warningMsg}</p>
                <div className={styles.btnRow}>
                  <button className={styles.backBtn} onClick={handleReset}>← Назад</button>
                </div>
              </div>
            )}

            {/* Data + transport photo */}
            {step === 'data' && driverData && (
              <div className={styles.cards}>
                {/* Данные водителя */}
                <div className={`${styles.card} ${styles.cardDone} ${styles.cardData}`}>
                  <span className={styles.cardIcon}>✅</span>
                  <div className={styles.driverInfo}>
                    <div className={styles.driverRow}><strong>Водитель:</strong> {driverData.employee_name || '-'}</div>
                    <div className={styles.driverRow}><strong>Телефон:</strong> {driverData.employee_phone || '-'}</div>
                    <div className={styles.driverRow}><strong>Машина:</strong> {driverData.vehicle_number || '-'}{driverData.vehicle_model ? ` (${driverData.vehicle_model})` : ''}</div>
                    <div className={styles.driverRow}><strong>База:</strong> {driverData.top_base_name || driverData.chpp_name || driverData.private_point_name || '-'}</div>
                    {lastLoadA && <div className={styles.driverRow}><strong>Погрузка №:</strong> {lastLoadA.id}</div>}
                  </div>
                </div>

                {/* Фото транспорта */}
                <div
                  className={`${styles.card} ${transportImageSrc ? styles.cardDone : styles.cardActive}`}
                  onClick={() => photoInputRef.current?.click()}
                >
                  {transportImageSrc ? (
                    <>
                      <img src={transportImageSrc} alt="Транспорт" className={styles.cardThumb} />
                      <span className={styles.cardLabel}>Фото сделано ✓</span>
                      <span className={styles.cardSub}>Нажмите для пересъёмки</span>
                    </>
                  ) : (
                    <>
                      <span className={styles.cardIcon}>🚛</span>
                      <span className={styles.cardLabel}>Нажмите, чтобы<br />сфотографировать транспорт</span>
                    </>
                  )}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={handlePhotoFileChange}
                  />
                </div>

                {sendError && <p className={styles.sendError}>{sendError}</p>}

                <button
                  className={styles.confirmBtn}
                  disabled={!transportBlob || sending}
                  onClick={handleConfirm}
                >
                  {sending ? '⏳ Отправка...' : 'Подтвердить прибытие'}
                </button>
              </div>
            )}

            {/* Done */}
            {step === 'done' && (
              <div className={styles.infoBlock}>
                <span className={styles.infoIcon}>✅</span>
                <p className={styles.infoText}>Прибытие успешно подтверждено!</p>
                <div className={styles.btnRow}>
                  <button className={styles.confirmBtn} onClick={handleClose}>Закрыть</button>
                </div>
              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
};

const StepDot = ({ num, active, done }) => (
  <div style={{
    width: 28, height: 28, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 13, flexShrink: 0, transition: 'all 0.2s',
    background: done ? '#198754' : active ? '#0d6efd' : '#dee2e6',
    color: (done || active) ? '#fff' : '#6c757d',
  }}>
    {done ? '✓' : num}
  </div>
);

export default React.memo(OperatorScanModal);
